/**
 * 簡易予測 (WMA 系) の設定ダイアログで、取込元日付の picker が表示中の 6 週 (42 日) の
 * うち実績の無い日 (simple-methods AC-DFM-014 / 017) を取得する hook。
 *
 * 判定は backend が既定の取込元日付の選定と同じ述語で行う。frontend は返った日を
 * 選べなくするだけで、実績の有無を自分で判定しない。
 */
import { useMemo } from 'react'

import { create } from '@bufbuild/protobuf'
import { useQuery } from '@connectrpc/connect-query'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { AggregationScope } from '@ebalance/proto/bffuser/v1/demand_confirm_record_model_pb'
import { listSimpleForecastUnavailableSourceDates } from '@ebalance/proto/bffuser/v1/demand_forecast_service-DemandForecastService_connectquery'
import {
  ListSimpleForecastUnavailableSourceDatesRequestSchema,
  type SimpleForecastMethod,
} from '@ebalance/proto/bffuser/v1/demand_forecast_service_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'

import { dayGridRange } from '~/components/common/DayGrid'
import {
  civilDateToInputValue,
  civilDateToProtoDate,
  dateToCivilDate,
  protoDateToCivilDate,
} from '~/models/civilDate'

/** picker の表示月 (`month` は 1 始まり)。 */
export type SourceDateViewMonth = { year: number; month: number }

type Params = {
  caseId: string | undefined
  areaCode: AreaCode | undefined
  targetDate: ProtoDate | undefined
  method: SimpleForecastMethod | undefined
  targetScope: 'BG_TOTAL' | 'RETAILER' | 'UNATTRIBUTED'
  retailerBusinessCd: string | undefined
  /** 未定 (picker をまだ開いていない) なら取得しない。 */
  viewMonth: SourceDateViewMonth | undefined
  enabled: boolean
}

export type UnavailableSourceDates = {
  /** 表示中の範囲を取得中か。取得中はその範囲の日を選べなくする。 */
  isLoading: boolean
  /** 実績の無い日の `YYYY-MM-DD`。取得失敗時は空 (= 選べる。fail-open)。 */
  dateKeys: ReadonlySet<string>
}

const EMPTY: ReadonlySet<string> = new Set()

/**
 * 取得失敗は全画面エラーにせず、選べる日として扱う (失敗で
 * 全日を不可にするとダイアログが使えなくなる。漏れた日は取込時に AC-DFM-008 で止まる)。
 * global QueryClient の `throwOnError: true` を本 query だけ `false` に上書きする
 * (docs/guides/frontend-coding-guide.yaml「queries は throwOnError: true」からの意図的な逸脱)。
 */
export function useListSimpleForecastUnavailableSourceDates({
  caseId,
  areaCode,
  targetDate,
  method,
  targetScope,
  retailerBusinessCd,
  viewMonth,
  enabled,
}: Params): UnavailableSourceDates {
  const range =
    viewMonth !== undefined
      ? dayGridRange(viewMonth.year, viewMonth.month)
      : undefined
  const canFetch =
    enabled &&
    range !== undefined &&
    caseId !== undefined &&
    areaCode !== undefined &&
    targetDate !== undefined &&
    method !== undefined

  const { data, isLoading } = useQuery(
    listSimpleForecastUnavailableSourceDates,
    create(ListSimpleForecastUnavailableSourceDatesRequestSchema, {
      caseId,
      areaCode: areaCode ?? 0,
      targetDate,
      method: method ?? 0,
      scope:
        targetScope === 'BG_TOTAL'
          ? AggregationScope.BG_TOTAL
          : AggregationScope.RETAIL,
      retailerBusinessCd:
        targetScope === 'RETAILER' ? retailerBusinessCd : undefined,
      fromDate:
        range !== undefined
          ? civilDateToProtoDate(dateToCivilDate(range.from))
          : undefined,
      toDate:
        range !== undefined
          ? civilDateToProtoDate(dateToCivilDate(range.to))
          : undefined,
    }),
    {
      enabled: canFetch,
      throwOnError: false,
    },
  )

  const dateKeys = useMemo(
    () =>
      data === undefined
        ? EMPTY
        : new Set(
            data.unavailableDates.map((d) =>
              civilDateToInputValue(protoDateToCivilDate(d)),
            ),
          ),
    [data],
  )

  return { isLoading: canFetch && isLoading, dateKeys }
}
